import {
  BookingEditForm,
  FormViewType,
} from '@/components/forms/BookingFormFactory/BookingEditForm';
import { useOperatorsOrdersQuery } from 'api/queries/operators';
import { colors, ModalBase, PriceDisplay } from 'remote/global';
import { useTranslation } from 'react-i18next';
import { Stack, Button } from '@mui/material';
import { useEffect, useState } from 'react';

enum ModalContextType {
  ORDER_VIEW = 'order',
  RIDE_VIEW = 'ride',
}

type Props = {
  open: boolean;
  onClose: () => void;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isOrderEditAvailable: boolean;
  bookingNumber: string;
  rideId?: number;
  contextType: ModalContextType;
  onOpenOrderView?: () => void;
};

function OrderEditModal({
  open,
  setOpen,
  onClose,
  bookingNumber,
  isOrderEditAvailable,
  rideId,
  contextType,
  onOpenOrderView,
}: Props) {
  const { t } = useTranslation();
  const orderQuery = useOperatorsOrdersQuery(open ? bookingNumber : undefined);
  const { data: order, isLoading } = orderQuery;
  const [currentRideId, setCurrentRideId] = useState<number | undefined>(rideId);

  useEffect(() => {
    if (open) {
      setCurrentRideId(rideId);
    }
  }, [open, rideId]);

  if (isLoading || !order) return null;

  const selectedRide =
    contextType === ModalContextType.RIDE_VIEW
      ? order.rides.find((ride) => ride.id === currentRideId)
      : order.rides.find((ride) => ride.id === currentRideId); // For order view, if a ride is selected

  // Logic for group switching (only relevant for RIDE_VIEW)
  const rideNumber = selectedRide?.rideNumber;
  const groupNumber = selectedRide?.groupNumber;

  const ridesWithSameRideNumber =
    contextType === ModalContextType.RIDE_VIEW && selectedRide
      ? order.rides.filter((ride) => ride.rideNumber === rideNumber)
      : [];
  const uniqueGroups = Array.from(new Set(ridesWithSameRideNumber.map((ride) => ride.groupNumber)));

  const handleGroupSwitch = (groupNo: number) => {
    const targetRide = ridesWithSameRideNumber.find((ride) => ride.groupNumber === groupNo);
    if (targetRide) {
      setCurrentRideId(targetRide.id);
    }
  };

  if (contextType === ModalContextType.RIDE_VIEW && !selectedRide) return null;

  // Separate variable for the title to resolve the type error
  const title: React.ReactNode =
    contextType === ModalContextType.RIDE_VIEW ? (
      <Button
        variant='text'
        onClick={() => {
          setOpen(false);
          onOpenOrderView?.();
        }}
        sx={{ textTransform: 'none', fontWeight: '600', padding: '0' }}
      >
        #{bookingNumber}
      </Button>
    ) : (
      `#${bookingNumber}`
    );

  return (
    <ModalBase
      open={open}
      setOpen={setOpen}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      title={title}
      header={{
        children: (
          <PriceDisplay
            priceExclVat={order.operatorPriceExclVat}
            priceInclVat={order.operatorPriceInclVat}
            unadjustedPriceExclVat={order.unadjustedPriceExclVat}
            unadjustedPriceInclVat={order.unadjustedPriceInclVat}
            currency={order.currency}
          />
        ),
      }}
    >
      {/* Group switching buttons only for RIDE_VIEW and if multiple groups exist */}
      {contextType === ModalContextType.RIDE_VIEW && uniqueGroups.length > 1 && (
        <Stack direction='row' spacing={1} mb={2} px={3}>
          {uniqueGroups.map((groupNo) => (
            <Button
              key={groupNo}
              variant={groupNo === groupNumber ? 'contained' : 'outlined'}
              size='small'
              onClick={() => handleGroupSwitch(groupNo)}
              sx={{
                height: '26px',
                borderRadius: '6px',
                border: `1px solid ${colors.primary} !important`,
                fontSize: '12px',
              }}
            >
              {t('terms.attributes.ride.groupNo', { groupNo })}
            </Button>
          ))}
        </Stack>
      )}

      {/* Render the combined form */}
      <BookingEditForm
        orderQuery={orderQuery}
        isOrderEditAvailable={isOrderEditAvailable}
        rideId={contextType === ModalContextType.RIDE_VIEW ? currentRideId : rideId}
        onClose={onClose}
        viewType={
          contextType === ModalContextType.ORDER_VIEW ? FormViewType.ORDER : FormViewType.RIDE
        }
      />
    </ModalBase>
  );
}

export { OrderEditModal, ModalContextType };
